import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';

/**
 * ログインページ初期化コンポーネント
 *
 * @element kd-login-page-init
 * @description ログインページの初期化処理を行う。
 *              - registerSuccess: 会員登録完了メッセージの表示
 *              - redirectCart: ログイン後カートへリダイレクトするhidden input追加
 *              - ページ内リンクのスムーススクロール（ヘッダー高さ補正）
 */
@customElement('kd-login-page-init')
export class KdLoginPageInit extends LitElement {
  createRenderRoot() {
    return this;
  }

  private static HEADER_HEIGHT = 73;

  private static ANIMATION_DURATION = 500;

  private handleAnchorClick = (e: Event): void => {
    const anchor = e.currentTarget as HTMLAnchorElement;
    const href = anchor.getAttribute('href');
    if (!href || !href.startsWith('#')) return;

    // #recover / #login はログイン ⇔ パスワード回復フォームの表示切替を
    // main.css の `:target` セレクタ（#recover:target ~ #login など）に委ねている。
    // preventDefault するとハッシュ遷移が起きず :target が発火せず切替できないため、
    // この 2 リンクだけはネイティブのハッシュ遷移に任せる（スムーススクロールはしない）。
    if (href === '#recover' || href === '#login') return;

    const targetSelector = href === '#' || href === '' ? 'html' : href;
    const target = document.querySelector(targetSelector);
    if (!target) return;

    e.preventDefault();
    const rect = target.getBoundingClientRect();
    const position = rect.top + window.scrollY - KdLoginPageInit.HEADER_HEIGHT;
    window.scrollTo({ top: position, behavior: 'smooth' });
  };

  private anchors: HTMLAnchorElement[] = [];

  connectedCallback(): void {
    super.connectedCallback();

    // registerSuccess: 会員登録完了メッセージ表示
    if (localStorage.getItem('registerSuccess')) {
      const loginForm = document.querySelector('#customer_login');
      if (loginForm) {
        const msg = document.createElement('span');
        msg.classList.add('tw:text-[#464C55]', 'tw:font-bold');
        msg.textContent = '会員登録が完了しました。ログインしてください。';
        loginForm.parentElement?.insertBefore(msg, loginForm);
      }
      localStorage.removeItem('registerSuccess');
    }

    // redirectCart: ログイン後カートへリダイレクト
    if (localStorage.getItem('redirectCart')) {
      const loginForm = document.querySelector('form#customer_login');
      if (loginForm) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'checkout_url';
        input.value = '/cart';
        loginForm.prepend(input);
      }
      localStorage.removeItem('redirectCart');
    }

    // ページ内リンクのスムーススクロール
    this.anchors = Array.from(document.querySelectorAll('a[href^="#"]'));
    for (const anchor of this.anchors) {
      anchor.addEventListener('click', this.handleAnchorClick);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    for (const anchor of this.anchors) {
      anchor.removeEventListener('click', this.handleAnchorClick);
    }
    this.anchors = [];
  }

  render() {
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-login-page-init': KdLoginPageInit;
  }
}
